import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, Globe } from "lucide-react";
import logoNeuroResearch from "@/assets/logo-neuroresearch.webp";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { useLanguage } from "@/contexts/LanguageContext";
import { ThemeToggle } from "@/components/ThemeToggle";

const Header = () => {
  const { language, setLanguage, t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  // Route mapping for all languages
  const routeMap: Record<string, Record<string, string>> = {
    // Home pages
    '/': { es: '/', en: '/en', de: '/de', pt: '/pt', fr: '/fr' },
    '/en': { es: '/', en: '/en', de: '/de', pt: '/pt', fr: '/fr' },
    '/de': { es: '/', en: '/en', de: '/de', pt: '/pt', fr: '/fr' },
    '/pt': { es: '/', en: '/en', de: '/de', pt: '/pt', fr: '/fr' },
    '/fr': { es: '/', en: '/en', de: '/de', pt: '/pt', fr: '/fr' },
    // Research Projects
    '/investigacion-proyectos': { es: '/investigacion-proyectos', en: '/en/research-projects', de: '/de/forschungsprojekte', pt: '/pt/projetos-pesquisa', fr: '/fr/projets-recherche' },
    '/en/research-projects': { es: '/investigacion-proyectos', en: '/en/research-projects', de: '/de/forschungsprojekte', pt: '/pt/projetos-pesquisa', fr: '/fr/projets-recherche' },
    '/de/forschungsprojekte': { es: '/investigacion-proyectos', en: '/en/research-projects', de: '/de/forschungsprojekte', pt: '/pt/projetos-pesquisa', fr: '/fr/projets-recherche' },
    '/pt/projetos-pesquisa': { es: '/investigacion-proyectos', en: '/en/research-projects', de: '/de/forschungsprojekte', pt: '/pt/projetos-pesquisa', fr: '/fr/projets-recherche' },
    '/fr/projets-recherche': { es: '/investigacion-proyectos', en: '/en/research-projects', de: '/de/forschungsprojekte', pt: '/pt/projetos-pesquisa', fr: '/fr/projets-recherche' },
    // Clinical Trials
    '/ensayos-clinicos': { es: '/ensayos-clinicos', en: '/en/clinical-trials', de: '/de/klinische-studien', pt: '/pt/ensaios-clinicos', fr: '/fr/essais-cliniques' },
    '/en/clinical-trials': { es: '/ensayos-clinicos', en: '/en/clinical-trials', de: '/de/klinische-studien', pt: '/pt/ensaios-clinicos', fr: '/fr/essais-cliniques' },
    '/de/klinische-studien': { es: '/ensayos-clinicos', en: '/en/clinical-trials', de: '/de/klinische-studien', pt: '/pt/ensaios-clinicos', fr: '/fr/essais-cliniques' },
    '/pt/ensaios-clinicos': { es: '/ensayos-clinicos', en: '/en/clinical-trials', de: '/de/klinische-studien', pt: '/pt/ensaios-clinicos', fr: '/fr/essais-cliniques' },
    '/fr/essais-cliniques': { es: '/ensayos-clinicos', en: '/en/clinical-trials', de: '/de/klinische-studien', pt: '/pt/ensaios-clinicos', fr: '/fr/essais-cliniques' },
    // Collaborations
    '/colaboraciones': { es: '/colaboraciones', en: '/en/collaborations', de: '/de/zusammenarbeit', pt: '/pt/colaboracoes', fr: '/fr/collaborations' },
    '/en/collaborations': { es: '/colaboraciones', en: '/en/collaborations', de: '/de/zusammenarbeit', pt: '/pt/colaboracoes', fr: '/fr/collaborations' },
    '/de/zusammenarbeit': { es: '/colaboraciones', en: '/en/collaborations', de: '/de/zusammenarbeit', pt: '/pt/colaboracoes', fr: '/fr/collaborations' },
    '/pt/colaboracoes': { es: '/colaboraciones', en: '/en/collaborations', de: '/de/zusammenarbeit', pt: '/pt/colaboracoes', fr: '/fr/collaborations' },
    '/fr/collaborations': { es: '/colaboraciones', en: '/en/collaborations', de: '/de/zusammenarbeit', pt: '/pt/colaboracoes', fr: '/fr/collaborations' },
    // Volunteering
    '/voluntariado': { es: '/voluntariado', en: '/en/volunteering', de: '/de/freiwilligenarbeit', pt: '/pt/voluntariado', fr: '/fr/benevolat' },
    '/en/volunteering': { es: '/voluntariado', en: '/en/volunteering', de: '/de/freiwilligenarbeit', pt: '/pt/voluntariado', fr: '/fr/benevolat' },
    '/de/freiwilligenarbeit': { es: '/voluntariado', en: '/en/volunteering', de: '/de/freiwilligenarbeit', pt: '/pt/voluntariado', fr: '/fr/benevolat' },
    '/pt/voluntariado': { es: '/voluntariado', en: '/en/volunteering', de: '/de/freiwilligenarbeit', pt: '/pt/voluntariado', fr: '/fr/benevolat' },
    '/fr/benevolat': { es: '/voluntariado', en: '/en/volunteering', de: '/de/freiwilligenarbeit', pt: '/pt/voluntariado', fr: '/fr/benevolat' },
    // Events
    '/eventos': { es: '/eventos', en: '/en/events', de: '/de/veranstaltungen', pt: '/pt/eventos', fr: '/fr/evenements' },
    '/en/events': { es: '/eventos', en: '/en/events', de: '/de/veranstaltungen', pt: '/pt/eventos', fr: '/fr/evenements' },
    '/de/veranstaltungen': { es: '/eventos', en: '/en/events', de: '/de/veranstaltungen', pt: '/pt/eventos', fr: '/fr/evenements' },
    '/pt/eventos': { es: '/eventos', en: '/en/events', de: '/de/veranstaltungen', pt: '/pt/eventos', fr: '/fr/evenements' },
    '/fr/evenements': { es: '/eventos', en: '/en/events', de: '/de/veranstaltungen', pt: '/pt/eventos', fr: '/fr/evenements' },
    // Newsletter
    '/newsletter': { es: '/newsletter', en: '/en/newsletter', de: '/de/newsletter', pt: '/pt/newsletter', fr: '/fr/newsletter' },
    '/en/newsletter': { es: '/newsletter', en: '/en/newsletter', de: '/de/newsletter', pt: '/pt/newsletter', fr: '/fr/newsletter' },
    '/de/newsletter': { es: '/newsletter', en: '/en/newsletter', de: '/de/newsletter', pt: '/pt/newsletter', fr: '/fr/newsletter' },
    '/pt/newsletter': { es: '/newsletter', en: '/en/newsletter', de: '/de/newsletter', pt: '/pt/newsletter', fr: '/fr/newsletter' },
    '/fr/newsletter': { es: '/newsletter', en: '/en/newsletter', de: '/de/newsletter', pt: '/pt/newsletter', fr: '/fr/newsletter' },
    // Contact
    '/contacto': { es: '/contacto', en: '/en/contact', de: '/de/kontakt', pt: '/pt/contato', fr: '/fr/contact' },
    '/en/contact': { es: '/contacto', en: '/en/contact', de: '/de/kontakt', pt: '/pt/contato', fr: '/fr/contact' },
    '/de/kontakt': { es: '/contacto', en: '/en/contact', de: '/de/kontakt', pt: '/pt/contato', fr: '/fr/contact' },
    '/pt/contato': { es: '/contacto', en: '/en/contact', de: '/de/kontakt', pt: '/pt/contato', fr: '/fr/contact' },
    '/fr/contact': { es: '/contacto', en: '/en/contact', de: '/de/kontakt', pt: '/pt/contato', fr: '/fr/contact' },
    // Privacy
    '/privacidad': { es: '/privacidad', en: '/en/privacy', de: '/de/datenschutz', pt: '/pt/privacidade', fr: '/fr/privacy' },
    '/en/privacy': { es: '/privacidad', en: '/en/privacy', de: '/de/datenschutz', pt: '/pt/privacidade', fr: '/fr/privacy' },
    '/de/datenschutz': { es: '/privacidad', en: '/en/privacy', de: '/de/datenschutz', pt: '/pt/privacidade', fr: '/fr/privacy' },
    '/pt/privacidade': { es: '/privacidad', en: '/en/privacy', de: '/de/datenschutz', pt: '/pt/privacidade', fr: '/fr/privacy' },
    '/fr/privacy': { es: '/privacidad', en: '/en/privacy', de: '/de/datenschutz', pt: '/pt/privacidade', fr: '/fr/privacy' },
    // Donation Success
    '/donation-success': { es: '/donation-success', en: '/en/donation-success', de: '/de/donation-success', pt: '/pt/donation-success', fr: '/fr/donation-success' },
    '/en/donation-success': { es: '/donation-success', en: '/en/donation-success', de: '/de/donation-success', pt: '/pt/donation-success', fr: '/fr/donation-success' },
    '/de/donation-success': { es: '/donation-success', en: '/en/donation-success', de: '/de/donation-success', pt: '/pt/donation-success', fr: '/fr/donation-success' },
    '/pt/donation-success': { es: '/donation-success', en: '/en/donation-success', de: '/de/donation-success', pt: '/pt/donation-success', fr: '/fr/donation-success' },
    '/fr/donation-success': { es: '/donation-success', en: '/en/donation-success', de: '/de/donation-success', pt: '/pt/donation-success', fr: '/fr/donation-success' },
  };

  const handleLanguageChange = (newLang: 'es' | 'en' | 'de' | 'pt' | 'fr') => {
    const currentPath = location.pathname;
    const targetPath = routeMap[currentPath]?.[newLang];
    
    setLanguage(newLang);
    
    if (targetPath) {
      navigate(targetPath);
    } else {
      // Fallback to home page of the selected language
      const homePaths: Record<string, string> = { es: '/', en: '/en', de: '/de', pt: '/pt', fr: '/fr' };
      navigate(homePaths[newLang]);
    }
  };

  const scrollToDonationSection = () => {
    const isHomePage = location.pathname === '/' || location.pathname === '/en' || location.pathname === '/de' || location.pathname === '/pt' || location.pathname === '/fr';
    
    if (isHomePage) {
      const donationSection = document.getElementById('donation-section');
      if (donationSection) {
        donationSection.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      const prefix = language === 'en' ? '/en' : language === 'de' ? '/de' : language === 'pt' ? '/pt' : language === 'fr' ? '/fr' : '';
      window.location.href = `${prefix}/#donation-section`;
    }
  };

  const getNavItems = () => {
    const prefix = language === 'en' ? '/en' : language === 'de' ? '/de' : language === 'pt' ? '/pt' : language === 'fr' ? '/fr' : '';
    
    let paths;
    if (language === 'de') {
      paths = {
        inicio: '/',
        investigacion: '/forschungsprojekte',
        ensayos: '/klinische-studien',
        colaboraciones: '/zusammenarbeit',
        voluntariado: '/freiwilligenarbeit',
        eventos: '/veranstaltungen',
        newsletter: '/newsletter',
        contacto: '/kontakt'
      };
    } else if (language === 'pt') {
      paths = {
        inicio: '/',
        investigacion: '/projetos-pesquisa',
        ensayos: '/ensaios-clinicos',
        colaboraciones: '/colaboracoes',
        voluntariado: '/voluntariado',
        eventos: '/eventos',
        newsletter: '/newsletter',
        contacto: '/contato'
      };
    } else if (language === 'en') {
      paths = {
        inicio: '/',
        investigacion: '/research-projects',
        ensayos: '/clinical-trials',
        colaboraciones: '/collaborations',
        voluntariado: '/volunteering',
        eventos: '/events',
        newsletter: '/newsletter',
        contacto: '/contact'
      };
    } else if (language === 'fr') {
      paths = {
        inicio: '/',
        investigacion: '/projets-recherche',
        ensayos: '/essais-cliniques',
        colaboraciones: '/collaborations',
        voluntariado: '/benevolat',
        eventos: '/evenements',
        newsletter: '/newsletter',
        contacto: '/contact'
      };
    } else {
      paths = {
        inicio: '/',
        investigacion: '/investigacion-proyectos',
        ensayos: '/ensayos-clinicos',
        colaboraciones: '/colaboraciones',
        voluntariado: '/voluntariado',
        eventos: '/eventos',
        newsletter: '/newsletter',
        contacto: '/contacto'
      };
    }

    return [
      { href: prefix + paths.inicio, label: t('nav.inicio') },
      { href: prefix + paths.ensayos, label: t('nav.ensayos') },
      { href: prefix + paths.colaboraciones, label: t('nav.colaboraciones') },
      { href: prefix + paths.investigacion, label: t('nav.investigacion') },
      { href: prefix + paths.voluntariado, label: t('nav.voluntariado') },
      { href: prefix + paths.eventos, label: t('nav.eventos') },
      { href: prefix + paths.newsletter, label: t('nav.newsletter') },
      { href: prefix + paths.contacto, label: t('nav.contacto') },
    ];
  };

  const navItems = getNavItems();

  const languages = [
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'pt', name: 'Português', flag: '🇵🇹' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
  ];

  return (
    <header className="sticky top-0 z-50 w-full">
      {/* Top section - Orange background for logo */}
      <div className="bg-accent-orange">
        <div className="container flex justify-center py-3">
          <Link to="/" className="flex items-center">
            <img 
              src={logoNeuroResearch} 
              alt="NeuroResearch Foundation" 
              width={350}
              height={94}
              loading="eager"
              decoding="async"
              className="h-20 md:h-24 w-auto"
            />
          </Link>
        </div>
      </div>

      {/* Bottom section - Black background for navigation */}
      <div className="bg-foreground dark:bg-background border-b border-border">
        <div className="container flex items-center justify-between py-2">
          {/* Desktop Navigation - Centered */}
          <nav className="hidden lg:flex items-center justify-center flex-1 space-x-6">
            {navItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className="text-sm font-medium text-background dark:text-foreground transition-colors hover:text-primary-light"
              >
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Action Buttons */}
          <div className="flex items-center space-x-2 ml-auto lg:ml-0">
            {/* Theme Toggle */}
            <ThemeToggle />
            
            {/* Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" aria-label={t('nav.selectLanguage') || 'Select language'} className="text-background dark:text-foreground hover:bg-white/10">
                  <Globe className="h-4 w-4 mr-1" />
                  {languages.find(lang => lang.code === language)?.flag}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => handleLanguageChange(lang.code as 'es' | 'en' | 'de' | 'pt' | 'fr')}
                    className={language === lang.code ? "bg-accent" : ""}
                  >
                    <span className="mr-2">{lang.flag}</span>
                    {lang.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Donate Button */}
            <Button onClick={scrollToDonationSection} className="hidden md:flex">
              {t('nav.donar')}
            </Button>

            {/* Mobile Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="sm" aria-label={t('nav.menu') || 'Menu'} className="text-background dark:text-foreground hover:bg-white/10">
                  <Menu className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                {navItems.map((item) => (
                  <DropdownMenuItem key={item.href} asChild>
                    <Link to={item.href}>{item.label}</Link>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuItem onClick={scrollToDonationSection}>
                  {t('nav.donar')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;